import { CameraMonitor } from "@faro-lotv/lotv";
import { useFrame, useThree } from "@react-three/fiber";
import { JSX, useEffect, useRef, useState } from "react";
import {
  BufferGeometry,
  Material,
  MathUtils,
  Matrix4,
  Mesh,
  PlaneGeometry,
  Vector3,
} from "three";
import { GUIDED_NAVIGATION_PARAMS as P } from "../tools/guided-navigation/guided-navigation-params";
import {
  GuidedNavigationState,
  nowSeconds,
} from "../tools/guided-navigation/guided-navigation-state";
import { useToolsAcquaintance } from "../tools/tools-acquaintance-context";
import { buildCorridorFloorGeometry } from "./guided-navigation-floor-geometry";
import {
  createCorridorFloorMaterial,
  createGridWallMaterial,
  GUIDED_NAVIGATION_GUIDES_NAME,
} from "./guided-navigation-materials";

/** Size of the grid wall at the corridor boundary */
const BOUNDARY_WALL_SIZE = { width: 2.5, height: 2.5 };

/** Size of the grid wall in front of the splats */
const SPLAT_WALL_SIZE = { width: 1.5, height: 1.5 };

/** Opacity of the corridor floor */
const FLOOR_OPACITY = 0.2;

const Z_AXIS = new Vector3(0, 0, 1);
const Y_AXIS = new Vector3(0, 1, 0);

/** Squared length below which a wall axis is degenerate */
const WALL_AXIS_EPSILON_SQ = 1e-6;

const WALL_X = new Vector3();
const WALL_Y = new Vector3();
const WALL_BASIS = new Matrix4();

/** @returns the guides of the guided navigation through Gaussian Splats, if active */
export function GuidedNavigationRenderer(): JSX.Element | null {
  const { guidedNavigation, isGuidedNavigationActive } = useToolsAcquaintance();
  if (!isGuidedNavigationActive) return null;
  return <GuidedNavigationGuides state={guidedNavigation} />;
}

type GuidedNavigationGuidesProps = {
  /** The guided navigation state to render */
  state: GuidedNavigationState;
};

/**
 * Renders the corridor floor, visible only while the movement is constrained or after a click on a point
 * that is not allowed, and the grid walls appearing as the camera
 * approaches the corridor boundary or the splats.
 *
 * @returns the guides of the guided navigation
 */
function GuidedNavigationGuides({
  state,
}: GuidedNavigationGuidesProps): JSX.Element {
  const camera = useThree((s) => s.camera);

  const [floor] = useState(() => {
    const mesh = createGuideMesh(
      new BufferGeometry(),
      createCorridorFloorMaterial(),
    );
    // The cosmetics pass never clears the stencil buffer, which the floor material uses to draw each pixel once
    mesh.onBeforeRender = (renderer) => renderer.clearStencil();
    return mesh;
  });
  const [boundaryWall] = useState(() =>
    createGuideMesh(
      new PlaneGeometry(BOUNDARY_WALL_SIZE.width, BOUNDARY_WALL_SIZE.height),
      createGridWallMaterial(
        BOUNDARY_WALL_SIZE.width,
        BOUNDARY_WALL_SIZE.height,
      ),
    ),
  );
  const [splatWall] = useState(() =>
    createGuideMesh(
      new PlaneGeometry(SPLAT_WALL_SIZE.width, SPLAT_WALL_SIZE.height),
      createGridWallMaterial(SPLAT_WALL_SIZE.width, SPLAT_WALL_SIZE.height),
    ),
  );

  const [verticalSplatWall] = useState(() =>
    createGuideMesh(
      new PlaneGeometry(SPLAT_WALL_SIZE.width, SPLAT_WALL_SIZE.height),
      createGridWallMaterial(SPLAT_WALL_SIZE.width, SPLAT_WALL_SIZE.height),
    ),
  );

  useEffect(
    () => () => {
      for (const { geometry, material } of [
        floor,
        boundaryWall,
        splatWall,
        verticalSplatWall,
      ]) {
        geometry.dispose();
        material.dispose();
      }
    },
    [floor, boundaryWall, splatWall, verticalSplatWall],
  );

  const floorVersion = useRef(-1);
  const floorElevation = useRef(NaN);
  const floorFade = useRef(0);
  const wallFade = useRef(0);
  const [lookTarget] = useState(() => new Vector3());
  const [cameraMonitor] = useState(() => new CameraMonitor());

  useFrame((_, delta) => {
    const { output } = state;
    const { uniforms } = floor.material;
    cameraMonitor.checkCameraMovement(camera, delta);

    floorFade.current = MathUtils.clamp(
      floorFade.current +
        (state.shouldShowCorridor(nowSeconds())
          ? delta / P.fadeInDuration
          : -delta / P.fadeOutDuration),
      0,
      1,
    );
    wallFade.current = MathUtils.clamp(
      wallFade.current +
        (state.shouldShowWalls(nowSeconds())
          ? delta / P.fadeInDuration
          : -delta / P.fadeOutDuration),
      0,
      1,
    );

    // The trajectories close in elevation to the camera are selected again only once it stopped, as it is expensive
    const cameraY = camera.position.y;
    if (
      floorVersion.current !== state.segmentsVersion ||
      (!cameraMonitor.cameraMoving &&
        Math.abs(cameraY - floorElevation.current) >=
          P.corridorRebuildThreshold)
    ) {
      floorVersion.current = state.segmentsVersion;
      floorElevation.current = cameraY;
      floor.geometry.dispose();
      floor.geometry = buildCorridorFloorGeometry(
        state.getCorridorSegments(cameraY),
        P.corridorRadius,
      );
    }
    floor.position.y = cameraY - P.corridorFloorOffset;

    uniforms.uOpacity.value = floorFade.current * FLOOR_OPACITY;
    floor.visible = floorFade.current > 0 && !!floor.geometry.index?.count;

    const boundaryOpacity = output.hasBoundary
      ? output.boundaryGridProximity * wallFade.current
      : 0;
    boundaryWall.visible = boundaryOpacity > 0;
    boundaryWall.material.uniforms.uOpacity.value = boundaryOpacity;
    if (boundaryWall.visible) {
      boundaryWall.position.copy(output.boundaryPoint);
      // The grid lines follow the trajectory direction, so the wall shows its slope
      const normal = output.boundaryNormal;
      WALL_X.copy(output.boundaryTangent).addScaledVector(
        normal,
        -normal.dot(output.boundaryTangent),
      );
      if (WALL_X.lengthSq() < WALL_AXIS_EPSILON_SQ) {
        WALL_X.crossVectors(Y_AXIS, normal);
      }
      if (WALL_X.lengthSq() < WALL_AXIS_EPSILON_SQ) WALL_X.set(1, 0, 0);
      WALL_X.normalize();
      WALL_Y.crossVectors(normal, WALL_X);
      boundaryWall.quaternion.setFromRotationMatrix(
        WALL_BASIS.makeBasis(WALL_X, WALL_Y, normal),
      );
    }

    // The hits are discarded when the camera stops, so the walls rely on the last guidance to fade out
    const splatOpacity = output.splat.gridProximity * wallFade.current;
    splatWall.visible = splatOpacity > 0;
    splatWall.material.uniforms.uOpacity.value = splatOpacity;
    if (splatWall.visible) {
      splatWall.position.copy(output.splat.point);
      splatWall.lookAt(camera.position);
    }

    const verticalOpacity =
      output.verticalSplat.gridProximity * wallFade.current;
    verticalSplatWall.visible = verticalOpacity > 0;
    verticalSplatWall.material.uniforms.uOpacity.value = verticalOpacity;
    if (verticalSplatWall.visible) {
      verticalSplatWall.position.copy(output.verticalSplat.point);
      // Horizontal wall facing the camera, as lookAt is degenerate for a vertical view
      verticalSplatWall.quaternion.setFromUnitVectors(
        Z_AXIS,
        lookTarget.copy(state.verticalSplatHit.direction).negate(),
      );
    }
  });

  return (
    <group name={GUIDED_NAVIGATION_GUIDES_NAME}>
      <primitive object={floor} />
      <primitive object={boundaryWall} />
      <primitive object={splatWall} />
      <primitive object={verticalSplatWall} />
    </group>
  );
}

/**
 * @returns a hidden mesh ignored by raycasts
 * @param geometry the mesh geometry
 * @param material the mesh material
 */
function createGuideMesh<G extends BufferGeometry, M extends Material>(
  geometry: G,
  material: M,
): Mesh<G, M> {
  const mesh = new Mesh(geometry, material);
  mesh.raycast = () => {};
  mesh.visible = false;
  return mesh;
}
